<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div v-if="visible" class="orangehrm-card-container expensa-section">
    <div class="orangehrm-header-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('expensa.pres_title') }}
      </oxd-text>
      <oxd-button
        v-if="actions.canRebuild"
        display-type="secondary"
        :label="$t('expensa.pres_rebuild')"
        :disabled="busy"
        @click="onRebuild"
      />
    </div>
    <div class="expensa-notice">
      <oxd-text>{{ $t('expensa.pres_notice') }}</oxd-text>
    </div>
    <oxd-text v-if="isLoading && !loaded" class="expensa-label">
      {{ $t('expensa.loading') }}
    </oxd-text>
    <oxd-text v-else-if="loadError && !loaded" class="expensa-error">
      {{ $t('expensa.pres_load_failed') }}
    </oxd-text>
    <oxd-text v-else-if="packages.length === 0" class="expensa-label">
      {{ $t('expensa.pres_empty') }}
    </oxd-text>
    <template v-else>
      <oxd-text v-if="reloadError" class="expensa-error">
        {{ $t('expensa.pres_load_failed') }}
      </oxd-text>
      <ul class="expensa-packages">
        <li v-for="pkg in views" :key="pkg.version">
          <div class="expensa-package-head">
            <oxd-text class="expensa-package-title">
              {{ $t('expensa.pres_version') }} {{ pkg.version }}
            </oxd-text>
            <status-pill :label="statusLabel(pkg)" :tone="pkg.tone" />
            <oxd-button
              v-if="actions.canDownloadZip"
              display-type="ghost"
              class="orangehrm-sm-button"
              :label="$t('expensa.pres_download_zip')"
              :disabled="busy"
              @click="onDownloadZip(pkg.version)"
            />
          </div>
          <oxd-text class="expensa-label">
            {{ $t('expensa.pres_created_on') }}: {{ instant(pkg.createdAt) }}
            <template v-if="pkg.timestampedAt">
              - {{ $t('expensa.pres_timestamped_on') }}:
              {{ instant(pkg.timestampedAt) }}
            </template>
          </oxd-text>
          <oxd-text v-if="pkg.summary.ok" class="expensa-ok">
            {{ $t('expensa.pres_verify_ok') }}
          </oxd-text>
          <ul v-else class="expensa-problems">
            <li v-if="pkg.summary.indexBad">
              {{ $t('expensa.pres_verify_index_bad') }}
            </li>
            <li v-else-if="pkg.summary.indexMismatch">
              {{ $t('expensa.pres_verify_index_mismatch') }}
            </li>
            <li v-if="pkg.summary.badFiles.length">
              {{ $t('expensa.pres_verify_bad_files') }}:
              {{ pkg.summary.badFiles.join(', ') }}
            </li>
            <li v-if="pkg.summary.missingFiles.length">
              {{ $t('expensa.pres_verify_missing_files') }}:
              {{ pkg.summary.missingFiles.join(', ') }}
            </li>
            <li v-if="pkg.summary.extraFiles.length">
              {{ $t('expensa.pres_verify_extra_files') }}:
              {{ pkg.summary.extraFiles.join(', ') }}
            </li>
            <li v-if="pkg.summary.timestampMismatch">
              {{ $t('expensa.pres_verify_timestamp_mismatch') }}
            </li>
          </ul>
        </li>
      </ul>
    </template>
  </div>

  <confirmation-dialog
    ref="rebuildDialog"
    :title="$t('expensa.pres_rebuild_title')"
    :subtitle="$t('expensa.pres_rebuild_text')"
    :cancel-label="$t('general.cancel')"
    :confirm-label="$t('expensa.pres_rebuild')"
    confirm-button-type="secondary"
  />
</template>

<script>
import ConfirmationDialog from '@/core/components/dialogs/ConfirmationDialog.vue';
import StatusPill from '@/orangehrmExpensaPlugin/components/StatusPill.vue';
import {expensaApi, errorMessage, fileUrl} from '../util/api';
import {downloadExport} from '../util/download';
import {instant} from '../util/format';
import {
  isSectionHidden,
  packageView,
  preservationActions,
} from '../util/preservation';

// Pacchetti di conservazione della nota. L'elenco lo restituisce solo a
// finance e al titolare: con 403/404 la sezione sparisce, con altri errori
// mostra un testo d'errore (mai "nessun pacchetto").
export default {
  name: 'ExpensaPreservationSection',
  components: {
    'confirmation-dialog': ConfirmationDialog,
    'status-pill': StatusPill,
  },
  props: {
    reportId: {
      type: Number,
      required: true,
    },
    reportStatus: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      packages: [],
      me: null,
      visible: true,
      isLoading: true,
      loaded: false,
      reloadError: false,
      loadError: false,
      busy: false,
    };
  },
  computed: {
    views() {
      return this.packages.map(packageView);
    },
    actions() {
      return preservationActions(this.reportStatus, this.me);
    },
  },
  beforeMount() {
    this.load();
  },
  methods: {
    instant,
    statusLabel(pkg) {
      return pkg.statusKey ? this.$t(pkg.statusKey) : pkg.status;
    },
    async load() {
      // Dopo il primo caricamento l'elenco resta visibile durante il reload.
      this.isLoading = true;
      this.loadError = false;
      this.reloadError = false;
      try {
        const [list, me] = await Promise.all([
          expensaApi(`/reports/${this.reportId}/preservation`).getAll(),
          expensaApi('/me')
            .getAll()
            .catch(() => null),
        ]);
        this.packages = list.data.data;
        this.me = me ? me.data.data : null;
        this.loaded = true;
      } catch (e) {
        if (!this.loaded && isSectionHidden(e)) {
          this.visible = false;
        } else if (this.loaded) {
          // reload fallito: i dati già mostrati restano, con un avviso
          this.reloadError = true;
        } else {
          this.loadError = true;
        }
      } finally {
        this.isLoading = false;
      }
    },
    async onDownloadZip(version) {
      if (this.busy) return;
      this.busy = true;
      try {
        await downloadExport(
          fileUrl(`/expensa/preservationZip/${this.reportId}/${version}`),
          `conservazione-${this.reportId}-v${version}.zip`,
          this.$t('expensa.pres_zip_failed'),
          this.$t('expensa.pres_session_expired'),
        );
        this.$toast.success({
          title: this.$t('general.success'),
          message: this.$t('expensa.pres_zip_done'),
        });
      } catch (e) {
        this.$toast.error({
          title: this.$t('general.error'),
          message: e instanceof Error ? e.message : errorMessage(e),
        });
      } finally {
        this.busy = false;
      }
    },
    async onRebuild() {
      if (this.busy) return;
      const choice = await this.$refs.rebuildDialog.showDialog();
      if (choice !== 'ok') return;
      this.busy = true;
      try {
        await expensaApi(`/reports/${this.reportId}/preservation`).create({});
        this.$toast.success({
          title: this.$t('general.success'),
          message: this.$t('expensa.pres_toast_rebuilt'),
        });
        await this.load();
      } catch (e) {
        this.$toast.error({
          title: this.$t('general.error'),
          message: errorMessage(e),
        });
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.expensa-section {
  margin-top: 1rem;
}
.expensa-label {
  font-size: 0.8rem;
  opacity: 0.7;
}
.expensa-error {
  color: #b3261e;
}
.expensa-ok {
  color: #1f7a45;
}
.expensa-notice {
  margin: 0.5rem 0 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background: #fff3d6;
  color: #8a5a00;
}
.expensa-packages {
  list-style: none;
  margin: 0;
  padding: 0;
  > li {
    padding: 0.5rem 0;
    border-bottom: 1px solid #e8eaef;
  }
}
.expensa-package-head {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.expensa-package-title {
  font-weight: 600;
}
.expensa-problems {
  margin: 0.25rem 0 0;
  padding-left: 1.2rem;
  color: #b3261e;
}
</style>
